import { useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { useStore } from '@tanstack/react-store'
import { useMemo } from 'react'

import { getWorkflowAuthoringSchema } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  GetWorkflowAuthoringSchemaRequestSchema,
  WorkflowTriggerEntityType,
  WorkflowTriggerEventType,
  type WorkflowEntityAuthoringSchema,
  type WorkflowFieldSchema,
  type WorkflowTriggerEventSchema,
  type WorkflowVariableSchema,
} from '@/gen/cortado/v1/cortado_pb'
import { organizationStore } from '@/stores/organization.store'

/**
 * Server-owned authoring vocabulary for the workflow builder: selectable
 * trigger events, per-entity condition fields, template variables, recipient
 * paths, branch fields, and updatable fields. Single source of truth — do not
 * reintroduce hardcoded frontend catalogs alongside this.
 */
export function useWorkflowAuthoringSchema(options?: { enabled?: boolean }) {
  const selectedOrganizationId = useStore(
    organizationStore,
    (state) => state.selectedOrganizationId
  )

  const query = useQuery(
    getWorkflowAuthoringSchema,
    create(GetWorkflowAuthoringSchemaRequestSchema, {
      organizationId: selectedOrganizationId ?? '',
    }),
    {
      enabled: (options?.enabled ?? true) && !!selectedOrganizationId,
      staleTime: 5 * 60 * 1000,
    }
  )

  return {
    schema: query.data,
    isLoading: query.isLoading,
    error: query.error,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/** Trigger events that have a wired backend publisher (selectable in the builder). */
export function selectableTriggerEvents(
  events: WorkflowTriggerEventSchema[] | undefined
): WorkflowTriggerEventSchema[] {
  return (events ?? []).filter((event) => event.publisherWired)
}

export function entitySchemaFor(
  entities: WorkflowEntityAuthoringSchema[] | undefined,
  entityType: WorkflowTriggerEntityType
): WorkflowEntityAuthoringSchema | undefined {
  return (entities ?? []).find((entity) => entity.entityType === entityType)
}

/**
 * Filter fields a trigger event adds on top of its entity's condition fields,
 * e.g. "Email subject" on "Email received". Empty for most events.
 */
export function eventConditionFieldsFor(
  events: WorkflowTriggerEventSchema[] | undefined,
  eventType: WorkflowTriggerEventType | undefined
): WorkflowFieldSchema[] {
  if (eventType === undefined || eventType === WorkflowTriggerEventType.UNSPECIFIED) return []
  return (events ?? []).find((event) => event.eventType === eventType)?.conditionFields ?? []
}

/** Convenience hook: the entity-scoped slice of the authoring schema. */
export function useEntityAuthoringSchema(
  entityType: WorkflowTriggerEntityType,
  eventType?: WorkflowTriggerEventType
) {
  const { schema, isLoading, hasLoaded, error } = useWorkflowAuthoringSchema({
    enabled: entityType !== WorkflowTriggerEntityType.UNSPECIFIED,
  })

  const entity = useMemo(
    () => entitySchemaFor(schema?.entities, entityType),
    [schema, entityType]
  )

  const eventFields = useMemo(
    () => eventConditionFieldsFor(schema?.triggerEvents, eventType),
    [schema, eventType]
  )
  const conditionFields = useMemo(
    () => [...eventFields, ...(entity?.conditionFields ?? [])],
    [eventFields, entity]
  )

  return {
    entity,
    // The event's own filter fields come first, then the entity's.
    conditionFields,
    templateVariables: (entity?.templateVariables ?? []) as WorkflowVariableSchema[],
    toFieldPaths: entity?.toFieldPaths ?? [],
    branchFields: entity?.branchFields ?? [],
    updateFields: entity?.updateFields ?? [],
    isLoading,
    hasLoaded,
    error,
  }
}
